/**
 * HighRiskLocationsWidget — locations ranked by risk, and what to do about them.
 *
 * The ranking reuses the application's OWN risk figures — hazard risk_level and
 * the residual-else-inherent risk score that HIRA approval gating already
 * relies on. No second risk calculation is introduced here; see
 * services/dashboard/analytics_safety.high_risk_locations for the precedence.
 *
 * The bar length is open high/critical hazards (assessed risk). The incident
 * count rides alongside in the row detail rather than in the bar, because
 * "assessed risk" and "how often something has already gone wrong" are
 * different measures and stacking them into one bar would say neither.
 *
 * Click a location → a tabbed investigation: the incidents there, the open
 * hazards there, and the risk assessments covering it.
 */
import React, { useState } from 'react';
import { MapPin, ShieldCheck } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { fetchHighRiskLocations } from '../api/client';
import ChartWidget from './ChartWidget';
import MultiDrilldownModal from './MultiDrilldownModal';
import { CARD, HEADING } from '../ui';

export default function HighRiskLocationsWidget({
  title = 'Top High-Risk Locations', limit = 10,
}) {
  const { siteId, time, departmentId, dimensions, fyStartMonth, refreshMs } = useDashboard();
  const [drill, setDrill] = useState(null);

  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';

  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { limit };
      if (siteId) body.site_id = siteId;
      if (departmentId) body.department_id = departmentId;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (time) body.time = time;
      if (fyStartMonth) body.fy_start_month = fyStartMonth;
      return fetchHighRiskLocations(body, { signal });
    },
    [siteId, timeKey, departmentId, dimsKey, limit, fyStartMonth],
    { refreshMs },
  );

  const rows = data?.data || [];
  const isEmpty = !loading && !error && rows.length === 0;
  // Only locations that have a value for the measure currently being ranked get
  // a bar. The server picks that measure by what the tenant actually records
  // (see `ranked_by`), but even then a location can be listed for evidence it
  // does not encode — a site with incidents but no assessed hazard risk. Drawing
  // those as zero-length bars produced a chart that looked broken next to a row
  // list plainly full of figures. They stay in the list, which carries every
  // number, and out of the chart, which carries one.
  const chartRows = rows.filter((r) => (r.value || 0) > 0);
  const unrankedCount = rows.length - chartRows.length;

  return (
    <>
      <div className={`${CARD} p-4`}>
        <h3 className={`${HEADING} mb-3 flex items-center gap-2`}>
          <MapPin className="h-4 w-4 text-amber-500" /> {title}
        </h3>

        {loading && <div className="h-56 animate-pulse rounded bg-gray-100" />}
        {!loading && error && (
          <div className="py-10 text-center text-sm text-red-600">
            Could not load location risk.
          </div>
        )}
        {isEmpty && (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            No location-level risk or incidents for the selected filters.
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            {chartRows.length > 0 ? (
              <ChartWidget
                type="hbar"
                data={chartRows}
                xKey="label"
                series={data?.series || [{ key: 'value', label: 'Risk' }]}
                height={Math.max(180, chartRows.length * 32 + 40)}
                showValues
                onPointClick={(d) => {
                  const row = d?.payload || d;
                  if (row?.label) setDrill(row);
                }}
              />
            ) : (
              <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                No location has a rankable risk figure yet — the locations below
                carry incidents or hazards, but no assessed risk score to chart.
              </div>
            )}

            {/* The numbers the bar deliberately does not encode — assessed risk
                and incident history are different measures, and one bar length
                cannot carry both honestly. */}
            {unrankedCount > 0 && chartRows.length > 0 && (
              <p className="mt-3 text-[11px] text-gray-500">
                {unrankedCount} further location{unrankedCount === 1 ? '' : 's'} below
                {' '}have incidents or hazards but no figure for the charted measure.
              </p>
            )}
            <ul className="mt-2 divide-y divide-gray-100 text-xs">
              {rows.map((r) => (
                <li key={r.key}>
                  <button
                    type="button"
                    onClick={() => setDrill(r)}
                    className="flex w-full items-center justify-between gap-3 py-1.5 text-left
                               hover:bg-blue-50/60 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                  >
                    <span className="truncate font-medium text-gray-700">{r.label}</span>
                    <span className="shrink-0 tabular-nums text-gray-500">
                      {r.open_hazards > 0 ? (
                        <>
                          {r.high_risk_hazards} high risk
                          <span className="mx-1 text-gray-300">·</span>
                          max score {r.max_risk_score ?? '—'}
                          <span className="mx-1 text-gray-300">·</span>
                        </>
                      ) : (
                        <>
                          no assessed risk
                          <span className="mx-1 text-gray-300">·</span>
                        </>
                      )}
                      {r.incidents} incident{r.incidents === 1 ? '' : 's'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {data?.basis && (
              <p className="mt-2 text-[11px] leading-relaxed text-gray-500">{data.basis}</p>
            )}
          </>
        )}
      </div>

      <MultiDrilldownModal
        open={!!drill}
        title={`Location: ${drill?.label ?? ''}`}
        subtitle="Investigate the risk, then the corrective actions. Click a row to open the record."
        tabs={drill ? [
          {
            id: 'incidents',
            label: `Incidents (${drill.incidents})`,
            metric: 'safety.incidents.total',
            extraDimensions: { location: [drill.key] },
          },
          {
            id: 'hazards',
            label: `Open Risks (${drill.open_hazards})`,
            metric: 'safety.hazards.open',
            extraDimensions: { location: [drill.key] },
            note: 'Hazards currently open at this location, from HIRA / risk assessments.',
          },
          {
            id: 'assessments',
            label: 'Risk Assessments',
            // The point-in-time metric, not the range one: what covers this
            // location TODAY, not what happened to be written this period.
            metric: 'risk.assessments.current',
            extraDimensions: { location: [drill.key] },
            note: 'Risk assessments currently in force for this location, whenever they were written — open one to review its hazards and controls.',
          },
        ] : []}
        onClose={() => setDrill(null)}
      />
    </>
  );
}
